import React, { FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, ToggleButton } from '@mui/material';
import FormInputNumber from '../FormInputNumber/FormInputNumber';
import Discount from '../../servicePaymentParts/Discount/Discount';
import { PreparedCalculateData } from '../../../models/Calculate';

interface FormInputMonthRefactorProps {
  name: string;
  step: number;
  min: number;
  max: number;
  handleInputMonthChange: (newValue: number | undefined) => void;
  isShownPresets: boolean;
  isHidden?: boolean;
  calculation: PreparedCalculateData | null;
  isShowDiscount: boolean;
  isDiscount50Available: boolean;
  isBonuses: boolean;
  isLoading: boolean;
  isAvailableForPaymentShare50Percent: boolean;
}

const FormInputMonthRefactor: FC<FormInputMonthRefactorProps> = ({
  handleInputMonthChange,
  name,
  step,
  max,
  min,
  isShownPresets,
  isHidden,
  calculation,
  isShowDiscount,
  isDiscount50Available,
  isBonuses,
  isLoading,
  isAvailableForPaymentShare50Percent,
}) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const discountPercent = calculation?.earn_bonus_percent || calculation?.view.dsc_percent || 0;
  const isDiscount = calculation && calculation?.discount_amount > 0 && !calculation?.earn_bonus_percent;

  const handleChangeMonth = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      handleInputMonthChange(Number(value) || undefined);
    },
    [handleInputMonthChange],
  );

  const btnsHandler = useCallback(
    (value: number) => {
      handleInputMonthChange(Number(value));
    },
    [handleInputMonthChange],
  );

  const handlePresets = useCallback(
    (_, value: number) => {
      handleInputMonthChange(value);
    },
    [handleInputMonthChange],
  );

  return (
    <FormControl component="fieldset" className={isHidden ? 'hidden' : ''}>
      <Controller
        name={name}
        control={control}
        render={({ field: { value } }) => {
          return (
            <>
              <FormInputNumber
                onChange={handleChangeMonth}
                value={value}
                minValue={min}
                maxValue={max}
                step={step}
                btnsHandler={btnsHandler}
                placeholder={min.toString()}
              />
              <div className="discount__amount">
                <Discount
                  discountPercent={discountPercent}
                  discountDefault={t('common.discountApplied')}
                  text={isDiscount ? t('common.discount') : t('common.bonusAccrual')}
                  isShowDiscount={isShowDiscount}
                  isDiscount50Available={isDiscount50Available && isAvailableForPaymentShare50Percent}
                  isBonuses={isBonuses && !isDiscount}
                  isLoading={isLoading}
                />
              </div>
              {isShownPresets && (
                <div className="discount__presets">
                  <ToggleButton
                    value={3}
                    onClick={handlePresets}
                    className={`discount__preset ${value === 3 ? 'active' : ''}`}
                  >
                    3 {t('serviceManagement.month')}
                  </ToggleButton>
                  <ToggleButton
                    value={6}
                    onClick={handlePresets}
                    className={`discount__preset ${value === 6 ? 'active' : ''}`}
                  >
                    6 {t('serviceManagement.month')}
                  </ToggleButton>
                  <ToggleButton
                    value={12}
                    onClick={handlePresets}
                    className={`discount__preset ${value === 12 ? 'active' : ''}`}
                  >
                    12 {t('serviceManagement.month')}
                  </ToggleButton>
                </div>
              )}
            </>
          );
        }}
      />
    </FormControl>
  );
};

export default memo(FormInputMonthRefactor);
